import React, { useState, useEffect } from "react";
import { apiV2Service } from "../services/apiV2Service";
import logger from "../utils/logger";
import { V2PasswordPolicy } from "../types/v2types";

interface SpaciaPasswordGuidelinesProps {
  className?: string;
}

/**
 * Reusable component to display password policy guidelines
 */
export const SpaciaPasswordGuidelines = ({ className = "" }: SpaciaPasswordGuidelinesProps) => {
  const [passwordPolicy, setPasswordPolicy] = useState<V2PasswordPolicy | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchPasswordPolicy = async () => {
      try {
        setLoading(true);
        const response = await apiV2Service.getDefaultPasswordPolicy();
        if (response.status === 200) {
          setPasswordPolicy(response.data.data);
        }
      } catch (error) {
        logger.error("Failed to fetch password policy:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPasswordPolicy();
  }, []);

  if (loading) {
    return <div className="mt-2 text-xs text-gray-400 animate-pulse">Loading requirements...</div>;
  }

  if (!passwordPolicy) {
    return <p className="mt-1 text-xs text-gray-500">Password should be at least 8 characters long</p>;
  }

  return (
    <div className={`mt-2 p-3 text-xs bg-gray-50 rounded-md border border-gray-100 ${className}`}>
      <p className="mb-2 font-medium text-gray-700 uppercase tracking-wider text-[10px]">Password Requirements:</p>
      <ul className="space-y-1.5 text-gray-600">
        <li className="flex items-center">
          <span className="mr-2 text-blue-500">•</span>
          Minimum length: <span className="mx-1 font-semibold text-gray-800">{passwordPolicy.minLength}</span>{" "}
          characters
        </li>
        {passwordPolicy.maxLength > 0 && (
          <li className="flex items-center">
            <span className="mr-2 text-blue-500">•</span>
            Maximum length: <span className="mx-1 font-semibold text-gray-800">{passwordPolicy.maxLength}</span>{" "}
            characters
          </li>
        )}
        {passwordPolicy.minUppercaseCount > 0 && (
          <li className="flex items-center">
            <span className="mr-2 text-blue-500">•</span>
            At least <span className="mx-1 font-semibold text-gray-800">{passwordPolicy.minUppercaseCount}</span>{" "}
            uppercase letter(s)
          </li>
        )}
        {passwordPolicy.minLowercaseCount > 0 && (
          <li className="flex items-center">
            <span className="mr-2 text-blue-500">•</span>
            At least <span className="mx-1 font-semibold text-gray-800">{passwordPolicy.minLowercaseCount}</span>{" "}
            lowercase letter(s)
          </li>
        )}
        {passwordPolicy.minDigitCount > 0 && (
          <li className="flex items-center">
            <span className="mr-2 text-blue-500">•</span>
            At least <span className="mx-1 font-semibold text-gray-800">{passwordPolicy.minDigitCount}</span> digit(s)
          </li>
        )}
        {passwordPolicy.minSymbolCount > 0 && (
          <li className="flex items-center">
            <span className="mr-2 text-blue-500">•</span>
            At least <span className="mx-1 font-semibold text-gray-800">{passwordPolicy.minSymbolCount}</span> special
            character(s)
          </li>
        )}
      </ul>
    </div>
  );
};
